import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { style } from './zui-shell-apps-css.js';
import { cache } from 'lit/directives/cache.js';
import { ZuiShellBase } from './zui-shell-base';
import '@zywave/zui-icons';
import type { PropertyValues } from 'lit';

/**
 * `<zui-shell-apps>` is our apps launchpad and is a global topbar feature. This is the primary method for jumping from one anchor solution to another. Consumers should set this up to only list products the user has access to. When adding this element, be sure to set its assigned slot to `apps` so it appears in the proper place in `<zui-shell-topbar>`.
 *
 * @element zui-shell-apps
 *
 * @slot - Default slot; list of all anchor solutions will be placed in here
 * @slot logo - Slot in logo elements or the default Zywave logo `<zui-logo>`, for proper brand placement
 */
export class ZuiShellApps extends ZuiShellBase {
  /**
   * Heading for the apps dropdown
   */
  @property({ type: String, attribute: 'app-header' })
  appHeader = 'Apps';

  /**
   * (optional): Link to view all apps in a separate location.
   * Set this property if you want the "See all" links
   * to appear at the bottom of your apps list.
   */
  @property({ type: String, attribute: 'all-apps-url' })
  allAppsUrl = '';

  /**
   * (optional): Text for the link to view all apps in a separate location.
   * Will not show up if `all-apps-url` is not set.
   */
  @property({ type: String, attribute: 'all-apps-text' })
  allAppsText = 'See all';

  /**
   * Set to true when the dropdown list of anchor solutions is visible
   */
  @property({ type: Boolean, reflect: true })
  open = false;

  firstUpdated(changedProps: PropertyValues) {
    super.firstUpdated(changedProps);
  }

  static get styles() {
    return [super.styles, style];
  }

  constructor() {
    super();
    this._observePageScrollChange = true;
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('click', (e) => {
      if (!e.composedPath().includes(this)) {
        this.open = false;
      }
    });
  }

  updated(changedProps: PropertyValues) {
    super.updated(changedProps);
  }

  render() {
    return html` ${cache(this._isMobile ? this._renderLogo() : this._renderApps())} `;
  }

  private _renderApps() {
    return html`
      <button class="apps" aria-label="Toggle apps dropdown" @click="${this._openApps}">
        <zui-icon class="apps-icon" icon="zui-shell-apps"></zui-icon>
        <div class="logo"> <slot name="logo"></slot> </div>
      </button>
      ${this._getSubmenuHtml()}
    `;
  }

  private _renderLogo() {
    return html`
      <div class="apps">
        <div class="logo"> <slot name="logo"></slot> </div>
      </div>
    `;
  }

  private _openApps() {
    const appItems = Array.from(this.querySelectorAll('zui-shell-apps-item'));
    if (appItems.length > 0 || !this.allAppsUrl) {
      this.open = !this.open;
    } else {
      window.location.href = this.allAppsUrl;
    }
  }

  private _getSubmenuHtml() {
    return html`
      <div class="apps-menu">
        <div class="header">${this.appHeader}</div>
        ${this._getAppsListHtml()}
      </div>
    `;
  }

  /**
   * @param {string} allAppsUrl Shows the link to view all apps at the very bottom of the apps list when set.
   */
  private _getAppsListHtml() {
    return html`
      <div class="apps-list">
        <slot></slot>
        ${this.allAppsUrl
          ? html`
              <div class="actions">
                <a class="view-all-apps" href="${this.allAppsUrl}">${this.allAppsText}</a>
              </div>
            `
          : ''}
      </div>
    `;
  }
}

window.customElements.define('zui-shell-apps', ZuiShellApps);

declare global {
  interface HTMLElementTagNameMap {
    'zui-shell-apps': ZuiShellApps;
  }
}
